/**
 * P-007 c9/c20 — local health-data encryption with a device-bound WebCrypto key.
 *
 * A non-extractable AES-GCM CryptoKey is generated ONCE per device and persisted in IndexedDB. Being
 * non-extractable, its raw bytes can never be read back by JavaScript (or an attacker with DOM
 * access) — the browser/OS keystore holds it — so cached PHI is encrypted at rest with a key bound
 * to this device. Every record is sealed with a fresh 96-bit IV. This replaces the previous
 * plaintext storage of health data in IndexedDB.
 */
const KEY_DB = 'healist-offline-key';
const KEY_STORE = 'keys';
const KEY_ID = 'device-key-v1';

function _openKeyDb() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(KEY_DB, 1);
    req.onupgradeneeded = () => {
      if (!req.result.objectStoreNames.contains(KEY_STORE)) req.result.createObjectStore(KEY_STORE);
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

function _idb(db, mode, fn) {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(KEY_STORE, mode);
    const store = tx.objectStore(KEY_STORE);
    const r = fn(store);
    r.onsuccess = () => resolve(r.result);
    r.onerror = () => reject(r.error);
  });
}

/** Return the device key, generating + persisting a non-extractable one on first use. */
export async function getDeviceKey() {
  const db = await _openKeyDb();
  let key = await _idb(db, 'readonly', (s) => s.get(KEY_ID));
  if (!key) {
    key = await crypto.subtle.generateKey({ name: 'AES-GCM', length: 256 }, false /* non-extractable */, ['encrypt', 'decrypt']);
    await _idb(db, 'readwrite', (s) => s.put(key, KEY_ID));
  }
  return key;
}

/** Encrypt a JSON-serializable value → { iv, ct } (both Uint8Array, structured-clone-safe for IndexedDB). */
export async function encryptRecord(value, key = null) {
  const k = key || (await getDeviceKey());
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const data = new TextEncoder().encode(JSON.stringify(value));
  const ct = new Uint8Array(await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, k, data));
  return { iv, ct };
}

/** Decrypt an { iv, ct } envelope back to the original value. */
export async function decryptRecord(envelope, key = null) {
  const k = key || (await getDeviceKey());
  const pt = await crypto.subtle.decrypt({ name: 'AES-GCM', iv: envelope.iv }, k, envelope.ct);
  return JSON.parse(new TextDecoder().decode(pt));
}
